import {
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { zoom as d3Zoom, zoomIdentity } from "d3-zoom";
import { scaleLinear } from "d3-scale";
import { select } from "d3-selection";
import "d3-transition"; // augments Selection with .transition()
import type { ZoomTransform } from "d3-zoom";
import type {
  LandscapePaper,
  LandscapeCamp,
  LandscapeTension,
} from "@/types/landscape";
import { ROLE_COLORS, READING_OPACITY, READING_OPACITY_DARK } from "@/lib/viz-colors";
import { clusterColor } from "@/lib/cluster-colors";
import type { ColorBy } from "@/stores/useLandscapeStore";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LandscapeCanvasProps {
  papers: LandscapePaper[];
  camps: LandscapeCamp[];
  tensions: LandscapeTension[];
  thesis: { x: number; y: number } | null;
  visibleRoles: Set<string>;
  /** Which dimension colors the dots. Role filter still applies in both modes. */
  colorBy: ColorBy;
  hoveredId: string | null;
  selectedId: string | null;
  onHover: (id: string | null) => void;
  onSelect: (id: string | null) => void;
  /**
   * When true, skip the dot-reveal fade-in animation and paint at final
   * opacity from the first frame. Set by LandscapeOverlay when this
   * concept's animation has already played in the current session, so
   * Expand→Back-to-Landscape doesn't replay the reveal on every return.
   */
  skipReveal?: boolean;
}

export interface LandscapeCanvasHandle {
  zoomToCamp: (campId: string) => void;
  /** Zoom to an arbitrary data-space centroid (used by topic-cluster rows). */
  zoomToRegion: (x: number, y: number) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAD = 48;
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 3;
const HIT_RADIUS = 14;
const POINT_MIN_R = 2.4;
const POINT_MAX_R = 9.5;
const ANIM_DURATION = 2500; // ms
const BEACON_PHASE_MS = 2700;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LandscapeCanvas = forwardRef<
  LandscapeCanvasHandle,
  LandscapeCanvasProps
>(function LandscapeCanvas(
  {
    papers,
    camps,
    tensions,
    thesis,
    visibleRoles,
    colorBy,
    hoveredId,
    selectedId,
    onHover,
    onSelect,
    skipReveal = false,
  },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number | null>(null);
  const transformRef = useRef<ZoomTransform>(zoomIdentity);
  const sizeRef = useRef({ w: 800, h: 500, dpr: 1 });
  const animDoneRef = useRef(false);

  // Mutable refs for values that change every frame
  const propsRef = useRef({
    visibleRoles,
    colorBy,
    hoveredId,
    selectedId,
    hoveredCampId: null as string | null,
  });
  propsRef.current.visibleRoles = visibleRoles;
  propsRef.current.colorBy = colorBy;
  propsRef.current.hoveredId = hoveredId;
  propsRef.current.selectedId = selectedId;

  // Dark mode is forced app-wide — the canvas always renders on the dark theme.
  const isDarkRef = useRef(true);

  // Pre-compute reveal order (distance from centroid)
  const revealOrder = useRef<{ paperId: string; revealAt: number }[]>([]);
  useEffect(() => {
    if (papers.length === 0) return;
    const cx = papers.reduce((s, p) => s + p.x, 0) / papers.length;
    const cy = papers.reduce((s, p) => s + p.y, 0) / papers.length;
    const withDist = papers.map((p) => ({
      paperId: p.paper_id,
      dist: Math.hypot(p.x - cx, p.y - cy),
    }));
    withDist.sort((a, b) => a.dist - b.dist);
    revealOrder.current = withDist.map((d, i) => ({
      paperId: d.paperId,
      revealAt: (i / papers.length) * 0.82,
    }));
    if (skipReveal) {
      // Concept already animated this session — paint at final opacity
      // from the first frame instead of replaying the 2.5s reveal.
      startRef.current = -ANIM_DURATION;
      animDoneRef.current = true;
    } else {
      startRef.current = null;
      animDoneRef.current = false;
    }
  }, [papers, skipReveal]);

  const revealMap = useRef(new Map<string, number>());
  useEffect(() => {
    const m = new Map<string, number>();
    for (const r of revealOrder.current) m.set(r.paperId, r.revealAt);
    revealMap.current = m;
  }, [papers]);

  // Score range across the displayed papers — dot size stretches across this
  // (relative) range, not the absolute [0,100], so the full radius span is
  // used even when the top-500 scores cluster in a narrow band.
  const scoreRangeRef = useRef({ min: 0, max: 100 });
  useEffect(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const p of papers) {
      if (p.normalized_score < min) min = p.normalized_score;
      if (p.normalized_score > max) max = p.normalized_score;
    }
    scoreRangeRef.current =
      max > min ? { min, max } : { min: 0, max: 100 };
  }, [papers]);

  // Paper lookup by id
  const paperMap = useRef(new Map<string, LandscapePaper>());
  useEffect(() => {
    const m = new Map<string, LandscapePaper>();
    for (const p of papers) m.set(p.paper_id, p);
    paperMap.current = m;
  }, [papers]);

  // Camp lookup
  const campMap = useRef(new Map<string, LandscapeCamp>());
  // Camp spread: radius in data-space that covers ~90% of the camp's papers
  const campSpread = useRef(new Map<string, number>());
  useEffect(() => {
    const m = new Map<string, LandscapeCamp>();
    for (const c of camps) m.set(c.id, c);
    campMap.current = m;

    // Compute spread per camp from paper positions
    const spread = new Map<string, number>();
    for (const camp of camps) {
      const members = papers.filter((p) => p.camp_id === camp.id);
      if (members.length < 2) {
        spread.set(camp.id, 0.15);
        continue;
      }
      const dists = members.map((p) =>
        Math.hypot(p.x - camp.centroid_x, p.y - camp.centroid_y),
      );
      dists.sort((a, b) => a - b);
      // Use 85th percentile distance as the spread radius
      const idx = Math.floor(dists.length * 0.85);
      spread.set(camp.id, Math.max(0.08, dists[idx]));
    }
    campSpread.current = spread;
  }, [camps]);

  // -----------------------------------------------------------------------
  // Coordinate helpers (depend on transform + size)
  // -----------------------------------------------------------------------

  const makeScales = useCallback(() => {
    const { w, h } = sizeRef.current;
    const t = transformRef.current;
    const sx = scaleLinear().domain([-1, 1]).range([PAD, w - PAD]);
    const sy = scaleLinear().domain([-1, 1]).range([h - PAD, PAD]); // flip Y
    return {
      x: (v: number) => t.applyX(sx(v)),
      y: (v: number) => t.applyY(sy(v)),
      k: t.k,
    };
  }, []);

  // -----------------------------------------------------------------------
  // Hit testing
  // -----------------------------------------------------------------------

  const hitTest = useCallback(
    (mx: number, my: number): string | null => {
      const scales = makeScales();
      const { dpr } = sizeRef.current;
      let best: string | null = null;
      let bestDist = HIT_RADIUS * dpr;
      for (const p of papers) {
        if (!propsRef.current.visibleRoles.has(p.argumentation_role)) continue;
        const px = scales.x(p.x) * dpr;
        const py = scales.y(p.y) * dpr;
        const d = Math.hypot(mx - px, my - py);
        if (d < bestDist) {
          bestDist = d;
          best = p.paper_id;
        }
      }
      return best;
    },
    [papers, makeScales],
  );

  // -----------------------------------------------------------------------
  // D3 Zoom
  // -----------------------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const zoomBehavior = d3Zoom<HTMLCanvasElement, unknown>()
      .scaleExtent([MIN_ZOOM, MAX_ZOOM])
      .on("zoom", (event) => {
        transformRef.current = event.transform;
      });
    select(canvas).call(zoomBehavior);

    // Expose for zoomToCamp
    (canvas as any).__zoom_behavior = zoomBehavior;

    return () => {
      select(canvas).on(".zoom", null);
    };
  }, []);

  // -----------------------------------------------------------------------
  // zoomToCamp imperative handle
  // -----------------------------------------------------------------------

  useImperativeHandle(
    ref,
    () => {
      const zoomToRegion = (dataX: number, dataY: number) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const { w, h } = sizeRef.current;
        const sx = scaleLinear().domain([-1, 1]).range([PAD, w - PAD]);
        const sy = scaleLinear().domain([-1, 1]).range([h - PAD, PAD]);
        const cx = sx(dataX);
        const cy = sy(dataY);
        const k = 1.8;
        const tx = w / 2 - cx * k;
        const ty = h / 2 - cy * k;
        const zoomBehavior = (canvas as any).__zoom_behavior;
        if (zoomBehavior) {
          select(canvas)
            .transition()
            .duration(600)
            .call(
              zoomBehavior.transform,
              zoomIdentity.translate(tx, ty).scale(k),
            );
        }
      };
      return {
        zoomToRegion,
        zoomToCamp: (campId: string) => {
          const camp = campMap.current.get(campId);
          if (!camp) return;
          zoomToRegion(camp.centroid_x, camp.centroid_y);
        },
      };
    },
    [camps],
  );

  // -----------------------------------------------------------------------
  // Mouse handlers
  // -----------------------------------------------------------------------

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const { dpr } = sizeRef.current;
      const mx = (e.clientX - rect.left) * dpr;
      const my = (e.clientY - rect.top) * dpr;

      // Paper hit test
      const id = hitTest(mx, my);
      onHover(id);

      // Camp hover detection (check proximity to camp centroid)
      const scales = makeScales();
      const pxPU = (scales.x(1) - scales.x(0));
      let closestCamp: string | null = null;
      let closestCampDist = Infinity;
      for (const camp of camps) {
        const spread = campSpread.current.get(camp.id) ?? 0.15;
        const hitR = Math.min(160, Math.max(50, spread * pxPU * 0.7)) * dpr;
        const cx = scales.x(camp.centroid_x) * dpr;
        const cy = scales.y(camp.centroid_y) * dpr;
        const d = Math.hypot(mx - cx, my - cy);
        if (d < hitR && d < closestCampDist) {
          closestCampDist = d;
          closestCamp = camp.id;
        }
      }
      propsRef.current.hoveredCampId = closestCamp;
    },
    [hitTest, onHover, camps, makeScales],
  );

  const handleClick = useCallback(() => {
    onSelect(propsRef.current.hoveredId);
  }, [onSelect]);

  const handleMouseLeave = useCallback(() => {
    onHover(null);
    propsRef.current.hoveredCampId = null;
  }, [onHover]);

  // -----------------------------------------------------------------------
  // Resize observer
  // -----------------------------------------------------------------------

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const dpr = window.devicePixelRatio || 1;
        sizeRef.current = { w: width, h: height, dpr };
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = width * dpr;
          canvas.height = height * dpr;
          canvas.style.width = `${width}px`;
          canvas.style.height = `${height}px`;
        }
      }
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  // -----------------------------------------------------------------------
  // Render loop
  // -----------------------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || papers.length === 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const draw = (timestamp: number) => {
      if (startRef.current === null) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const prog = animDoneRef.current
        ? 1
        : Math.min(1, elapsed / ANIM_DURATION);
      if (prog >= 1) animDoneRef.current = true;

      const { w, h, dpr } = sizeRef.current;
      const cw = w * dpr;
      const ch = h * dpr;
      ctx.clearRect(0, 0, cw, ch);
      ctx.save();
      ctx.scale(dpr, dpr);

      const scales = makeScales();
      const {
        visibleRoles: vr,
        colorBy: cb,
        hoveredId: hov,
        selectedId: sel,
        hoveredCampId,
      } = propsRef.current;
      const dark = isDarkRef.current;

      // --- Layer 1: Camp gradient regions ---
      const campAlphaBase = Math.min(1, prog * 1.8);
      const hasCampHover = hoveredCampId !== null;
      // Compute pixel-space scale factor: how many pixels per data-unit
      const pxPerUnit = (scales.x(1) - scales.x(0));
      for (const camp of camps) {
        const cx = scales.x(camp.centroid_x);
        const cy = scales.y(camp.centroid_y);
        const isHovered = hoveredCampId === camp.id;
        // Uniform compact radius at rest; true paper spread on hover
        const spread = campSpread.current.get(camp.id) ?? 0.15;
        const fullRadius = Math.max(60, spread * pxPerUnit * 0.85);
        const compactRadius = 70;
        const r = isHovered ? fullRadius : compactRadius;
        const baseAlpha = dark ? 0.22 : 0.18;
        const alpha =
          (isHovered ? baseAlpha * 1.28 : hasCampHover ? baseAlpha * 0.5 : baseAlpha) *
          campAlphaBase;
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        g.addColorStop(0, hexToRgba(camp.color, alpha * 1.5));
        g.addColorStop(0.5, hexToRgba(camp.color, alpha * 0.8));
        g.addColorStop(1, hexToRgba(camp.color, 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // --- Layer 2: Tension lines + labels ---
      ctx.save();
      ctx.setLineDash([3, 9]);
      ctx.lineWidth = 1;
      const tensionAlpha = Math.min(1, prog * 2) * (dark ? 0.2 : 0.12);
      const labelAlpha = Math.min(1, prog * 2) * (dark ? 0.28 : 0.18);
      ctx.font = `italic ${8 * scales.k}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      for (const t of tensions) {
        const ca = campMap.current.get(t.camp_a);
        const cb = campMap.current.get(t.camp_b);
        if (!ca || !cb) continue;
        const ax = scales.x(ca.centroid_x);
        const ay = scales.y(ca.centroid_y);
        const bx = scales.x(cb.centroid_x);
        const by = scales.y(cb.centroid_y);
        ctx.strokeStyle = dark
          ? `rgba(255,255,255,${tensionAlpha})`
          : `rgba(100,100,100,${tensionAlpha})`;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();
        // Label at midpoint
        if (t.tension_type) {
          const mx = (ax + bx) / 2;
          const my = (ay + by) / 2;
          ctx.fillStyle = dark
            ? `rgba(255,255,255,${labelAlpha})`
            : `rgba(100,100,100,${labelAlpha})`;
          ctx.fillText(t.tension_type, mx, my - 4 * scales.k);
        }
      }
      ctx.restore();

      // --- Layer 3: Camp labels ---
      ctx.font = `500 ${10 * scales.k}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      for (const camp of camps) {
        const cx = scales.x(camp.centroid_x);
        const cy = scales.y(camp.centroid_y);
        const isHovered = hoveredCampId === camp.id;
        const labelAlpha = Math.min(1, prog * 2.5) * (isHovered ? 0.9 : 0.5);
        ctx.fillStyle = dark
          ? `rgba(255,255,255,${labelAlpha})`
          : `rgba(30,30,30,${labelAlpha})`;
        ctx.fillText(camp.label, cx, cy - 80);
      }

      // --- Layer 4: Paper points ---
      for (const paper of papers) {
        if (!vr.has(paper.argumentation_role)) continue;
        const reveal = revealMap.current.get(paper.paper_id) ?? 0;
        const pp =
          prog < reveal ? 0 : Math.min(1, (prog - reveal) / 0.11);
        if (pp <= 0) continue;

        const px = scales.x(paper.x);
        const py = scales.y(paper.y);
        const col =
          cb === "cluster"
            ? clusterColor(paper.topic_cluster_id)
            : ROLE_COLORS[paper.argumentation_role] || "#888";
        const opacityMap = dark ? READING_OPACITY_DARK : READING_OPACITY;
        const baseAlpha =
          opacityMap[paper.reading_status] ?? opacityMap.unread;
        const alpha = baseAlpha * pp;
        const { min: scoreMin, max: scoreMax } = scoreRangeRef.current;
        const sNorm =
          scoreMax > scoreMin
            ? (paper.normalized_score - scoreMin) / (scoreMax - scoreMin)
            : 0.5;
        const rad =
          (POINT_MIN_R + sNorm * (POINT_MAX_R - POINT_MIN_R)) * scales.k;

        ctx.save();
        ctx.globalAlpha = alpha;

        // Layer 5: Bridge ring
        if (paper.is_bridge) {
          ctx.beginPath();
          ctx.arc(px, py, rad + 5 * scales.k, 0, Math.PI * 2);
          ctx.strokeStyle = dark ? "rgba(255,255,255,0.5)" : "rgba(100,100,100,0.35)";
          ctx.lineWidth = 1.2;
          ctx.stroke();
        }

        // Layer 6: Hover/selection ring
        const isH = hov === paper.paper_id;
        const isS = sel === paper.paper_id;
        if (isH || isS) {
          ctx.beginPath();
          ctx.arc(
            px,
            py,
            rad + (isS ? 7 : 5) * scales.k,
            0,
            Math.PI * 2,
          );
          ctx.strokeStyle = col;
          ctx.lineWidth = isS ? 2 : 1.5;
          ctx.globalAlpha = 1;
          ctx.stroke();
          ctx.globalAlpha = alpha;
        }

        // Point fill
        ctx.beginPath();
        ctx.arc(px, py, rad, 0, Math.PI * 2);
        ctx.fillStyle = col;
        ctx.fill();

        // Evidence indicator: small white inner dot
        if (paper.has_evidence) {
          ctx.globalAlpha = 1;
          ctx.beginPath();
          ctx.arc(px, py, Math.max(1.5, rad * 0.35), 0, Math.PI * 2);
          ctx.fillStyle = dark ? "#fff" : "#fff";
          ctx.fill();
        }

        ctx.restore();
      }

      // --- Layer 7: Thesis beacon ---
      if (thesis) {
        const beaconProg = Math.max(
          0,
          Math.min(1, (prog - 0.74) / 0.26),
        );
        if (beaconProg > 0) {
          const bx = scales.x(thesis.x);
          const by = scales.y(thesis.y);
          ctx.save();
          ctx.globalAlpha = beaconProg;

          // Pulsing rings
          for (let i = 0; i < 3; i++) {
            const phase =
              ((timestamp / BEACON_PHASE_MS + i * 0.333) % 1);
            const ringR = 12 + phase * 52 * scales.k;
            const ringAlpha = (1 - phase) * 0.45 * beaconProg;
            ctx.beginPath();
            ctx.arc(bx, by, ringR, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(186,117,23,${ringAlpha})`;
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }

          // Crosshair
          ctx.strokeStyle = `rgba(186,117,23,${0.88 * beaconProg})`;
          ctx.lineWidth = 1.5;
          const gap = 5;
          const arm = 12;
          for (const [x1, y1, x2, y2] of [
            [bx - arm, by, bx - gap, by],
            [bx + gap, by, bx + arm, by],
            [bx, by - arm, bx, by - gap],
            [bx, by + gap, bx, by + arm],
          ] as [number, number, number, number][]) {
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
          }

          // Center dot
          ctx.beginPath();
          ctx.arc(bx, by, 4.5 * scales.k, 0, Math.PI * 2);
          ctx.fillStyle = "#BA7517";
          ctx.fill();

          // Label
          ctx.font = `500 ${9 * scales.k}px system-ui, sans-serif`;
          ctx.fillStyle = `rgba(186,117,23,${0.82 * beaconProg})`;
          ctx.textAlign = "left";
          ctx.fillText("YOUR ARGUMENT", bx + 14 * scales.k, by + 4);

          ctx.restore();
        }
      }

      ctx.restore(); // undo dpr scale

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [papers, camps, tensions, thesis, makeScales]);

  return (
    <div ref={wrapRef} className="relative flex-1 min-w-0 overflow-hidden">
      <canvas
        ref={canvasRef}
        className="block size-full"
        style={{ cursor: hoveredId ? "pointer" : "default" }}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        onMouseLeave={handleMouseLeave}
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
